'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ProjectAgent } from '@/domains/agents/schemas/agent';
import { validateAgentModels, type InvalidModelEntry } from '@/domains/agents/utils/validate-agent-models';
import { ALL_AVAILABLE_MODELS } from '@/ai/models';

type ModelProvider = keyof typeof ALL_AVAILABLE_MODELS;

interface Replacement {
  provider: ModelProvider;
  modelName: string;
}

interface UseAgentModelValidationOptions {
  agents: ProjectAgent[];
  projectId: string;
  updateAgent: (agentId: string, updates: Partial<ProjectAgent>) => Promise<void>;
}

/**
 * Runs a background validation of all agent model configs on mount.
 * Returns state needed to drive the InvalidAgentModelsDialog.
 */
export function useAgentModelValidation({
  agents,
  projectId,
  updateAgent,
}: UseAgentModelValidationOptions) {
  const [invalidEntries, setInvalidEntries] = useState<InvalidModelEntry[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasValidated = useRef(false);

  // Run validation once after initial agents are loaded
  useEffect(() => {
    if (hasValidated.current || agents.length === 0) return;
    hasValidated.current = true;

    const invalid = validateAgentModels(agents);
    if (invalid.length > 0) {
      console.log(
        `[useAgentModelValidation] Found ${invalid.length} agent(s) with invalid models in project ${projectId}.`,
      );
      setInvalidEntries(invalid);
      setDialogOpen(true);
    }
  }, [agents, projectId]);

  const handleConfirm = useCallback(
    async (replacements: Map<string, Replacement>) => {
      // Group replacements by agent ID so we can batch step-override updates
      const agentUpdates = new Map<
        string,
        { topLevel?: Replacement; steps: Map<string, Replacement> }
      >();

      for (const entry of invalidEntries) {
        const key = entry.stepAgentId
          ? `${entry.agent.id}::${entry.stepAgentId}`
          : entry.agent.id;
        const replacement = replacements.get(key);
        if (!replacement) continue;

        if (!agentUpdates.has(entry.agent.id)) {
          agentUpdates.set(entry.agent.id, { steps: new Map() });
        }
        const bucket = agentUpdates.get(entry.agent.id)!;

        if (entry.stepAgentId) {
          bucket.steps.set(entry.stepAgentId, replacement);
        } else {
          bucket.topLevel = replacement;
        }
      }

      // Apply updates for each affected agent
      for (const [agentId, { topLevel, steps }] of agentUpdates) {
        const updates: Partial<ProjectAgent> = {};

        if (topLevel) {
          updates.modelProvider = topLevel.provider;
          updates.modelName = topLevel.modelName;
        }

        if (steps.size > 0) {
          // Find the current agent to merge step overrides
          const currentAgent = agents.find((a) => a.id === agentId);
          const existingOverrides = currentAgent?.stepOverrides ?? {};
          const newOverrides = { ...existingOverrides };

          for (const [stepId, rep] of steps) {
            newOverrides[stepId] = {
              ...newOverrides[stepId],
              modelProvider: rep.provider,
              modelName: rep.modelName,
            };
          }
          updates.stepOverrides = newOverrides;
        }

        await updateAgent(agentId, updates);
      }

      setDialogOpen(false);
      setInvalidEntries([]);
    },
    [invalidEntries, agents, updateAgent],
  );

  const handleDismiss = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return {
    invalidEntries,
    dialogOpen,
    handleConfirm,
    handleDismiss,
  };
}
